import { useEffect } from 'react';
import { etiquetasDe, SITE, type Meta } from './meta.js';

/**
 * A mesma meta, agora dentro do navegador.
 *
 * **Numa SPA o `<head>` é estado, e ninguém tratava dele.** O `index.html` é o
 * mesmo arquivo para todas as rotas: até 08/09/2026 a página de uma prova
 * dizia chamar-se "Titularis - questões comentadas...", e o `canonical` dela
 * apontava para a home - o que, para o buscador, é a página declarando ser uma
 * cópia da landing. Três provas com o mesmo título e o mesmo canônico não
 * disputam nada: o índice fica com uma e descarta as outras.
 *
 * **Quem renderiza JavaScript lê isto.** O Google e o Bing rodam a SPA e leem o
 * DOM final, então o que este hook escreve é o que eles indexam. Quem não roda
 * - WhatsApp, GPTBot, ClaudeBot - recebe o documento montado pela função da
 * Vercel, com o mesmo texto, vindo do mesmo `paginas.ts`.
 */

/**
 * O endereço do site, para montar canônico e `og:url` absolutos.
 *
 * Sai de uma constante, e não de `window.location.origin`: o canônico é uma
 * declaração sobre qual endereço é o oficial, e ler o endereço em que a pessoa
 * está transformaria a declaração em eco - qualquer domínio que sirva estes
 * arquivos passaria a se declarar canônico de si mesmo.
 */
export const origemDoSite = (): string => import.meta.env.VITE_SITE_URL ?? SITE;

/**
 * As tags que este hook manda, e portanto as que ele apaga antes de escrever.
 *
 * O `<title>` fica de fora da lista porque não se apaga um título: ele é
 * trocado por `document.title` logo abaixo, e remover o elemento deixaria a
 * aba sem nome no quadro entre uma rota e outra.
 */
const GERENCIADAS = [
  'link[rel="canonical"]',
  'meta[name="description"]',
  'meta[name="robots"]',
  'meta[name="author"]',
  'meta[property^="og:"]',
  'meta[name^="twitter:"]',
  'script[type="application/ld+json"]',
].join(', ');

/**
 * Escreve a meta no `<head>`, apagando a anterior.
 *
 * Apagar antes não é zelo, é correção: sem isso a notícia aberta depois da
 * listagem teria dois `og:title` e dois canônicos, e qual deles vale passa a
 * ser decisão de quem lê - o WhatsApp pega o primeiro, o Google costuma pegar
 * o último, e o cartão sai de uma página e o índice de outra.
 */
export function aplicarMeta(meta: Meta): void {
  for (const antiga of document.head.querySelectorAll(GERENCIADAS)) antiga.remove();

  for (const etiqueta of etiquetasDe(meta)) {
    switch (etiqueta.tag) {
      case 'title':
        document.title = etiqueta.texto;
        break;
      case 'meta': {
        const elemento = document.createElement('meta');
        elemento.setAttribute(etiqueta.chave, etiqueta.nome);
        elemento.setAttribute('content', etiqueta.conteudo);
        document.head.append(elemento);
        break;
      }
      case 'link': {
        const elemento = document.createElement('link');
        elemento.setAttribute('rel', etiqueta.rel);
        elemento.setAttribute('href', etiqueta.href);
        document.head.append(elemento);
        break;
      }
      case 'jsonld': {
        const elemento = document.createElement('script');
        elemento.type = 'application/ld+json';
        /*
         * `textContent`, e não `innerHTML`: o conteúdo já é JSON serializado, e
         * `innerHTML` faria o navegador interpretar `<` no meio de um título
         * como abertura de tag.
         */
        elemento.textContent = etiqueta.conteudo;
        document.head.append(elemento);
        break;
      }
    }
  }
}

/**
 * A meta desta tela, aplicada enquanto ela estiver montada.
 *
 * Aceita `null` **de propósito**: é o que a tela passa enquanto os dados não
 * chegaram. Escrever um título provisório ali seria escrever "Carregando" na
 * aba de quem compartilhou o link, e escrever `noindex` seria pior - uma API
 * lenta tiraria do índice uma notícia que existe. Quem rastreia lê o DOM
 * depois de ele assentar, então o quadro vazio é justamente o que ninguém vê.
 *
 * A chave da dependência é o JSON da meta, e não o objeto: a tela remonta o
 * objeto a cada render, e comparar por referência reescreveria o `<head>`
 * inteiro em toda passagem. Reidratar de dentro do efeito mantém o hook com
 * uma dependência só, que é a que de fato muda.
 */
export function useMeta(meta: Meta | null): void {
  const chave = meta ? JSON.stringify(meta) : null;

  useEffect(() => {
    if (!chave) return;
    aplicarMeta(JSON.parse(chave) as Meta);
  }, [chave]);
}
